import React from 'react';
import { useSelector } from 'react-redux';
import { BasicEnrollment, BasicMembership, BasicMentee, FlyoutState } from 'redux/schemas/models/course';
import { useAppDispatch } from 'redux/store';
import useInfiniteScroll from './use-infinite-scroll';

type Params = FlyoutState & {
  items: BasicMentee[] | BasicEnrollment[] | BasicMembership[];
  noItems: boolean;
};

type FetchItemsQueryParams = {
  page_size: number;
  sort_by?: string;
};

/**
 * Custom hook to handle the logic and states for the paginated flyout implementation
 * @param stateAppItemsProp Name of the state app property to get the pagination states
 * @param stateModelsItemsProp Name of the state models property to get the fetched items
 * @param fetchItemsQueryParams Params to be passed to the fetchItemsFn
 * @param fetchItemsFn Redux action to fetch new items
 * @returns Params for the paginated flyout state
 */
export const useFlyoutPaginated = (
  stateAppItemsProp: string,
  stateModelsItemsProp: string,
  fetchItemsQueryParams: FetchItemsQueryParams,
  fetchItemsFn: ({
    page,
    page_size,
    sort_by,
  }: { page: number } & FetchItemsQueryParams) => void,
): Params => {
  const dispatch = useAppDispatch();

  // Getting the pagination states from the state.app based on the 'stateAppItemsProp'
  const {
    [stateAppItemsProp]: {
      loading,
      loadingMore,
      firstPageLoaded,
      hasMore,
      ids,
      currentPage,
    },
  } = useSelector(state => state.app);

  // Getting the items from the state.models based on the 'stateModelsItemsProp'
  const items: BasicMentee[] | BasicEnrollment[] = useSelector(
    state => state.models[stateModelsItemsProp],
  );

  // Identifying if there aren't items
  const noItems = (!items || Object.keys(items)?.length === 0) && firstPageLoaded;

  // Using infinite scroll to load new items
  const scrollingElementRef: HTMLElement = document.querySelector(
    '.flyout-content',
  );
  const isPageBottom = useInfiniteScroll(scrollingElementRef, 20);

  React.useEffect(() => {
    const shouldLoad = ((isPageBottom && hasMore) || (currentPage === 1 && !firstPageLoaded))
      && !loading
      && !loadingMore;

    if (shouldLoad) {
      dispatch(fetchItemsFn({ page: currentPage, ...fetchItemsQueryParams }));
    }
  }, [hasMore, loading, loadingMore, isPageBottom, scrollingElementRef, currentPage]);

  return {
    items,
    noItems,
    loading,
    loadingMore,
    firstPageLoaded,
    hasMore,
    ids,
    currentPage,
  };
};

export default useFlyoutPaginated;
